import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { useAuth } from "../AuthProvider";
import { useToast } from "./ui/use-toast";
import {
  useGetSupportedMintsQuery,
  useGetWalletsQuery,
  Mint,
  Wallet,
} from "../generated/graphql";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet";
import QRCode from "react-qr-code";
import { ClipboardIcon } from "lucide-react";
import { MintSelect } from "./ui/MintSelect";
import { Label } from "./ui/label";
import { Button } from "./ui/button";

export function DepositSheet() {
  const { organizationId } = useAuth();
  const { toast } = useToast();

  const [selectedMint, setSelectedMint] = useState<Mint | null>(null);
  const [selectedWallet, setSelectedWallet] = useState<Wallet | null>(null);

  const [{ data: walletData, error: walletError }] = useGetWalletsQuery({
    variables: { organizationId: organizationId ?? "" },
    pause: organizationId === "" || !organizationId,
  });
  const [{ data: mintsData, error: mintsError }] = useGetSupportedMintsQuery();

  const wallets = walletData?.getWallets;
  const mints = mintsData?.getSupportedMints;

  useEffect(() => {
    if (walletError) {
      toast({
        title: "Error",
        description: walletError.message,
        variant: "destructive",
      });
    }

    if (mintsError) {
      toast({
        title: "Error",
        description: mintsError.message,
        variant: "destructive",
      });
    }
  }, [toast, walletError, mintsError]);

  useEffect(() => {
    if (selectedMint && wallets) {
      setSelectedWallet(
        wallets.find((w) => w.chain === selectedMint.chain) ?? null
      );
    }

    if (selectedMint === null) {
      setSelectedWallet(null);
    }
  }, [selectedMint, wallets]);

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Download className="w-6 h-6" />
      </SheetTrigger>
      <SheetContent
        side="right"
        className="flex justify-center items-center flex-col sm:mr-[60px] w-full sm:w-auto min-w-[350px]"
      >
        <SheetHeader>
          <SheetTitle>Deposit</SheetTitle>
        </SheetHeader>

        <div
          className="mb-4"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              marginBottom: "10px",
            }}
          >
            <Label style={{ marginBottom: "5px" }}>
              Select coin to deposit
            </Label>
            <MintSelect
              mintOptions={mints}
              onSelect={(m) => {
                setSelectedMint(m);
              }}
              selectedMint={selectedMint}
            />
          </div>

          {selectedWallet && (
            <div className="space-y-2 w-full sm:max-w-[300px] md:max-w-[400px] flex justify-center items-center flex-col mt-4">
              <p className="text-xs text-gray-600 mb-2 text-center">
                Send to the address below to receive credit for swap. Make sure
                to send a normal transaction from your wallet. Your funds will
                appear within the balances section.
              </p>

              <Button
                variant="outline"
                onClick={() => {
                  try {
                    navigator.clipboard.writeText(selectedWallet.address ?? "");
                    toast({
                      title: "Copied to clipboard",
                      description: "Address copied to clipboard",
                      variant: "default",
                    });
                  } catch (err) {
                    console.error("Failed to copy: ", err);
                    toast({
                      title: "Copy failed",
                      description: "Please copy the address manually",
                      variant: "destructive",
                    });
                  }
                }}
                className="w-fullh-auto py-2 px-3 text-left justify-start relative"
                style={{
                  marginBottom: "10px",
                  paddingRight: "40px",
                }}
              >
                <span
                  className="text-xs font-mono "
                  style={{
                    fontSize: "12px",
                  }}
                >
                  {selectedWallet.address}
                </span>
                <ClipboardIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 bg-white" />
              </Button>

              <div>
                <QRCode value={selectedWallet.address} size={128} />
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
